import { DeleteOutlined, PlusCircleOutlined } from "@ant-design/icons";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Button,
  Input,
  Form,
  Row,
  Col,
  message,
  Switch,
  Select,
  DatePicker,
  Flex,
  Breadcrumb,
} from "antd";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { Link, useNavigate } from "react-router-dom";
import { mutationOnErrorHandler } from "../../../utils/mutationUtils";
import { createOrderRequest } from "../../../api/requests/order";
import { useContacts } from "../../../api/hooks/customer";
import { useProducts } from "../../../api/hooks/product";
import WelcomeTopbar from "../../../components/common/header/WelcomeTopbar";
import OrderReceiverAddress from "./OrderReceiverAddress";
import { useEffect } from "react";
import {
  BIN_WIEGHT,
  BINS_COUNT_PER_PALLET,
  PALLET_WIEGHT,
} from "../../../constants";
import { useSettings } from "../../../api/hooks";

const initialItemDetail = {
  product: undefined,
  requiredQuantity: 0,
};

const createOrderSchemaResolver = yupResolver(
  yup.object().shape({
    carrier: yup.string(),
    customer: yup.string().required("Customer is required"),
    poNumber: yup.string(),
    requiredDate: yup.date().required("Delivery Date is required"),
    items: yup
      .array()
      .of(
        yup.object().shape({
          product: yup.string().required("Product is required"),
          requiredQuantity: yup
            .number()
            .required("Required Quantity is required")
            .positive("Required Quantity must be a positive number")
            .integer("Required Quantity must be an integer"),
        }),
      )
      .min(1, "Items must contain at least one item")
      .required("Items are required"),
    receiverAddress: yup.object().shape({
      name: yup.string(),
      address: yup.object().shape({
        street: yup.string(),
        city: yup.string(),
        state: yup.string(),
        zipCode: yup.string(),
        country: yup.string(),
      }),
    }),
  }),
);

function AddOrderPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { data: settingDetails } = useSettings();
  const { mutateAsync: createOrder } = useMutation({
    mutationFn: async (data) => {
      const res = await createOrderRequest(data);
      return res.data;
    },
    mutationKey: ["order/create"],
    onSuccess: (res) => {
      queryClient.invalidateQueries(["order"]);
      const successMessage = res?.message;
      if (successMessage) {
        message.success(successMessage);
      }
      navigate(-1);
    },
    onError: (error) => {
      mutationOnErrorHandler({ error, message });
    },
  });

  const { data: carrierList, isLoading: isLoadingCarrierList } = useContacts({
    params: {
      type: "CARRIER",
    },
  });
  const { data: customerList, isLoading: isLoadingCustomerList } = useContacts({
    params: {
      type: ["CUSTOMER", "PROCESSOR"],
    },
  });
  const { data: productList, isLoading: isLoadingProductList } = useProducts({
    params: {},
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm({
    resolver: createOrderSchemaResolver,
    defaultValues: {
      // active: true,
      items: [initialItemDetail],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "items",
  });

  // console.log("errors----->", errors);
  const items = watch("items");
  const binCount = items?.[0]?.requiredQuantity || 0;

  async function onSubmit(data) {
    if (!data?.receiverAddress?.name) {
      message.error(
        "Please select the warehouse address in order to submit the order",
      );
      return;
    }
    // console.log(data);
    const res = await createOrder(data);
    // console.log("res----->", res);
  }

  useEffect(() => {
    if (binCount && settingDetails?.shipping) {
      const {
        binWeight = BIN_WIEGHT,
        binsPerPallet = BINS_COUNT_PER_PALLET,
        palletWeight = PALLET_WIEGHT,
      } = settingDetails.shipping;
      const palletCount = Math.ceil(binCount / (binsPerPallet || 1));
      const totalWeight = palletCount * palletWeight + binCount * binWeight;
      setValue("palletCount", palletCount);
      setValue("orderWeight", totalWeight);
    } else {
      setValue("palletCount", 0);
      setValue("orderWeight", 0);
    }
  }, [binCount, settingDetails?.shipping]);

  return (
    <div className="flex flex-col">
      <WelcomeTopbar />
      <div className="flex flex-col gap-5 pb-6">
        <div className="flex flex-col gap-6">
          <Form layout="vertical" onFinish={handleSubmit(onSubmit)}>
            <div className="flex items-center justify-between rounded-t-xl bg-[#D1DFFF] px-6 py-3">
              <Breadcrumb
                items={[
                  {
                    title: <Link to={"/processes/order"}>Orders</Link>,
                  },
                  {
                    title: <div className="text-lg">Create</div>,
                  },
                ]}
              />
              <Button
                type="primary"
                htmlType="submit"
                className="flex items-center justify-center"
              >
                Submit Order
              </Button>
            </div>
            <div className="px-6 py-6">
              <Row gutter={18}>
                <Col span={12}>
                  <Form.Item
                    label="Customer"
                    name="customer"
                    validateStatus={errors.customer ? "error" : ""}
                    help={errors.customer && errors.customer.message}
                    required={true}
                    wrapperCol={{ sm: 24 }}
                    style={{
                      marginBottom: "8px",
                    }}
                  >
                    <Controller
                      control={control}
                      name="customer"
                      render={({ field }) => (
                        <Select
                          {...field}
                          showSearch
                          optionFilterProp="label"
                          placeholder="Customer"
                          allowClear
                          loading={isLoadingCustomerList}
                          options={customerList?.map(({ name, _id, type }) => ({
                            label: `${name} [${type}]`,
                            value: _id,
                          }))}
                          style={{
                            textTransform: "capitalize",
                          }}
                          dropdownStyle={{
                            textTransform: "capitalize",
                          }}
                        />
                      )}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="Carrier"
                    name="carrier"
                    validateStatus={errors.carrier ? "error" : ""}
                    help={errors?.carrier?.message}
                    // required={true}
                    wrapperCol={{ sm: 24 }}
                    style={{
                      marginBottom: "8px",
                    }}
                  >
                    <Controller
                      control={control}
                      name="carrier"
                      render={({ field }) => (
                        <Select
                          {...field}
                          showSearch
                          optionFilterProp="label"
                          placeholder="Carrier"
                          allowClear
                          loading={isLoadingCarrierList}
                          options={carrierList?.map(({ name, _id }) => ({
                            label: name,
                            value: _id,
                          }))}
                          style={{
                            textTransform: "capitalize",
                          }}
                          dropdownStyle={{
                            textTransform: "capitalize",
                          }}
                        />
                      )}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="Delivery Date"
                    name="requiredDate"
                    validateStatus={errors.requiredDate ? "error" : ""}
                    help={errors.requiredDate && errors.requiredDate.message}
                    required={true}
                    wrapperCol={{ sm: 24 }}
                  >
                    <Controller
                      control={control}
                      name="requiredDate"
                      render={({ field }) => (
                        <DatePicker
                          {...field}
                          style={{
                            width: "100%",
                          }}
                        />
                      )}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="Ship Date"
                    name="shipDate"
                    validateStatus={errors.shipDate ? "error" : ""}
                    help={errors?.shipDate?.message}
                    wrapperCol={{ sm: 24 }}
                  >
                    <Controller
                      control={control}
                      name="shipDate"
                      render={({ field }) => (
                        <DatePicker
                          {...field}
                          // ship date can not be past delivery date.
                          disabledDate={(current) =>
                            watch("requiredDate") &&
                            current.isAfter(watch("requiredDate"))
                          }
                          style={{
                            width: "100%",
                          }}
                        />
                      )}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="PO Number"
                    name="poNumber"
                    validateStatus={errors.poNumber ? "error" : ""}
                    help={errors?.poNumber?.message}
                  >
                    <Controller
                      control={control}
                      name="poNumber"
                      render={({ field }) => (
                        <Input {...field} placeholder="PO Number" />
                      )}
                    />
                  </Form.Item>
                </Col>

                <OrderReceiverAddress
                  errors={errors}
                  control={control}
                  watch={watch}
                  setValue={setValue}
                />

                <Col span={24}>
                  <p className="text-lg">Items</p>
                </Col>
                {fields.map((field, index) => {
                  return (
                    <Col span={24} key={field.id}>
                      <Row gutter={18}>
                        <Col span={12}>
                          <Form.Item
                            label="Product"
                            name={`items.${index}.product`}
                            validateStatus={
                              errors?.items?.[index]?.product ? "error" : ""
                            }
                            help={errors?.items?.[index]?.product?.message}
                            required={true}
                            wrapperCol={{ sm: 24 }}
                            className="flex-grow"
                          >
                            <Controller
                              control={control}
                              name={`items.${index}.product`}
                              render={({ field }) => (
                                <Select
                                  {...field}
                                  showSearch
                                  optionFilterProp="label"
                                  placeholder="Product"
                                  allowClear
                                  loading={isLoadingProductList}
                                  options={productList?.map(({ name, _id }) => {
                                    return {
                                      label: name,
                                      value: _id,
                                      disabled: items?.find(
                                        (item) => item?.product === _id,
                                      ),
                                    };
                                  })}
                                  style={{
                                    textTransform: "capitalize",
                                  }}
                                  dropdownStyle={{
                                    textTransform: "capitalize",
                                  }}
                                />
                              )}
                            />
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item
                            // className="w-full"
                            label="Required Quantity"
                            name={`items.${index}.requiredQuantity`}
                            validateStatus={
                              errors?.items?.[index]?.requiredQuantity
                                ? "error"
                                : ""
                            }
                            help={
                              errors?.items?.[index]?.requiredQuantity?.message
                            }
                            required={true}
                            className="flex-grow"
                          >
                            <Controller
                              control={control}
                              name={`items.${index}.requiredQuantity`}
                              render={({ field }) => (
                                <Input
                                  {...field}
                                  type="number"
                                  min={0}
                                  // step={0.01}
                                />
                              )}
                            />
                          </Form.Item>
                        </Col>

                        {/* <Button
                        key={field?.id}
                        onClick={() => {
                          remove(index);
                        }}
                        className="mb-6"
                      >
                        <DeleteOutlined />
                      </Button> */}
                      </Row>
                    </Col>
                  );
                })}
                {/* hide Add button (they only have one product at the moment) */}
                {/* <Col span={24}>
                  <Flex justify="flex-end" gap={10}>
                    <Button
                      htmlType="button"
                      onClick={() => {
                        append(initialItemDetail);
                      }}
                    >
                      Add
                    </Button>
                  </Flex>
                </Col> */}

                <Col span={12}>
                  <Form.Item label="Pallet Count" name="palletCount">
                    <Controller
                      control={control}
                      name="palletCount"
                      render={({ field }) => (
                        <Input
                          {...field}
                          type="number"
                          min={0}
                          placeholder="0"
                          disabled={true}
                        />
                      )}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label="Order Weight(lb)" name="orderWeight">
                    <Controller
                      control={control}
                      name="orderWeight"
                      render={({ field }) => (
                        <Input
                          {...field}
                          type="number"
                          min={0}
                          placeholder="0"
                          step="any"
                          disabled={true}
                        />
                      )}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </div>
          </Form>
        </div>
      </div>
    </div>
  );
}

export default AddOrderPage;
